import React, { useEffect, useState } from 'react'
import { DAY_MS, HOUR_MS, MINUTE_MS, SECOND_MS } from 'data/constants'

export type CountdownProps = {
    timestamp: number
    includeSeconds?: boolean
    renderCountdown: ({ countdownMs }: { countdownMs: number }) => React.ReactNode
}

export const Countdown = ({
    timestamp,
    includeSeconds = true,
    renderCountdown,
}: CountdownProps) => {
    const [distance, setDistance] = useState(
        () => getNextUpdate(timestamp, includeSeconds).distanceAtNextTick,
    )
    useEffect(() => {
        setDistance(timestamp - Date.now())
    }, [timestamp])

    useEffect(() => {
        const { nextTick, distanceAtNextTick } = getNextUpdate(timestamp, includeSeconds)

        if (distanceAtNextTick === 0) {
            return
        }

        const timeout = setTimeout(() => {
            setDistance(distanceAtNextTick)
        }, nextTick)

        return () => {
            clearTimeout(timeout)
        }
    }, [timestamp, includeSeconds])

    return renderCountdown({ countdownMs: distance })
}

const getNextUpdate = (timestamp: number, includeSeconds: boolean) => {
    const distance = timestamp - Date.now()
    if (distance <= 0) {
        return { nextTick: 0, distanceAtNextTick: 0, distance: 0 }
    }

    const granularity = (() => {
        if (distance <= MINUTE_MS) {
            return includeSeconds ? SECOND_MS : MINUTE_MS
        }
        if (distance <= HOUR_MS * 2) {
            return MINUTE_MS
        }
        return DAY_MS
    })()

    const nextTick = distance % granularity
    const distanceAtNextTick = Math.floor(distance / granularity) * granularity

    return { nextTick, distanceAtNextTick, distance }
}
